import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { Alert } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { useSoundCloudConnect } from '../../soundcloud/useSoundCloudConnect';
import { SoundCloudConnectResult, toSoundCloudConnectResult } from '../../soundcloud/soundCloudConnectResult';
import { SoundCloudReturnError } from '../../../smartLinks/catalog/soundCloud/soundCloudReturnOutcome';
import { soundCloudReturnMessages as m } from '../../../smartLinks/catalog/soundCloud/soundCloudReturnMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';

const ERROR_TEXT: Record<SoundCloudReturnError, { title: MessageDescriptor; alert: MessageDescriptor }> = {
  stateInvalid: { title: m.endedTitle, alert: m.endedAlert },
  refused: { title: m.refusedTitle, alert: m.refusedAlert },
  loginInUse: { title: m.failedTitle, alert: m.inUseAlert },
  failed: { title: m.failedTitle, alert: m.failedAlert },
};

/**
 * Reads `?soundCloud=` one time and strips it, so a reload shows no old banner (the same rule as
 * `useConnectReturn` of the social channels).
 */
function useSoundCloudConnectResult(onReturn: () => void) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [result, setResult] = useState<SoundCloudConnectResult | null>(null);
  const isRead = useRef(false);

  useEffect(() => {
    if (isRead.current) {
      return;
    }
    isRead.current = true;
    const value = toSoundCloudConnectResult(searchParams.get('soundCloud'));
    if (value === null) {
      return;
    }
    setResult(value);
    onReturn();
    const next = new URLSearchParams(searchParams);
    next.delete('soundCloud');
    setSearchParams(next, { replace: true });
  }, [onReturn, searchParams, setSearchParams]);

  const dismiss = useCallback(() => setResult(null), []);
  return { result, dismiss };
}

interface SoundCloudConnectAlertProps {
  accountId: number;
  /** Reloads the SoundCloud card after the sign-in came back. */
  onReturn: () => void;
}

/** The end of a SoundCloud sign-in on Channels (design soundcloud-connector b2, b4-b6). */
const SoundCloudConnectAlertComponent: React.FC<SoundCloudConnectAlertProps> = ({ accountId, onReturn }) => {
  const intl = useIntl();
  const { result, dismiss } = useSoundCloudConnectResult(onReturn);
  const soundCloud = useSoundCloudConnect({ accountId });
  const startAgain = useCallback(() => {
    soundCloud.connect().catch(console.error);
  }, [soundCloud]);

  if (result === null) {
    return null;
  }
  if (result === 'connected') {
    return (
      <Alert color="success" isOpen toggle={dismiss} data-testid="soundcloud-connect-alert-connected">
        <b>{intl.formatMessage(m.connectedTitle)}</b> {withBrandWord(intl.formatMessage(m.connectedBody))}
      </Alert>
    );
  }
  const text = ERROR_TEXT[result];
  return (
    <Alert color="danger" isOpen toggle={dismiss} data-testid="soundcloud-connect-alert-error">
      <b>{intl.formatMessage(text.title)}</b> {withBrandWord(intl.formatMessage(text.alert))}
      {result !== 'loginInUse' && (
        <div className="mt-2">
          <SMLButton variant="primary" onClick={startAgain} disabled={soundCloud.isConnecting}>
            {intl.formatMessage(result === 'stateInvalid' ? m.startAgain : m.tryAgain)}
          </SMLButton>
        </div>
      )}
    </Alert>
  );
};

export const SoundCloudConnectAlert = TypedMemo(SoundCloudConnectAlertComponent);
